import { Layer, ObjectCategory } from "@common/constants";
import { MUSEUM_ATTIC } from "@common/definitions/buildings";
import { Night } from "@common/definitions/night";
import { Vec, type Vector } from "@common/utils/vector";
import { Container, RenderTexture, Sprite, Texture } from "pixi.js";
import { Game } from "../game";
import { type Building } from "../objects/building";
import { type Obstacle } from "../objects/obstacle";
import { renderInto } from "../utils/canvasBlend";
import { PIXI_SCALE } from "../utils/constants";
import { toPixiCoords } from "../utils/pixi";
import {
    ATTIC_DARK_ZINDEX, ATTIC_LIGHT_ZINDEX, BULB_POOL, DARK, GLOW, GLOW_REACH, HALF_H, HALF_W, LIGHT_OUT, LIGHTS, LIT, RES,
    bulbShade, darknessAt, darkShows, flickerAt, inAtticArea, shadeTint
} from "./atticDarkLogic";
import { CameraManager } from "./cameraManager";
import { NightLight, nightTextures } from "./nightLight";
import { BEAM_LENGTH } from "./nightLightLogic";

export { ATTIC_DARK_ZINDEX, ATTIC_LIGHT_ZINDEX } from "./atticDarkLogic";

/*
 * The museum attic's dark (1.03; the owner, 24 October 2026: "Add an attic with flickering lights, dark atmosphere, no
 * room, just panels, scary atmosphere"; 1.04, 25 October 2026: "less light, also when moving between stairs and attic
 * the hwole attic shows up without darkness and flickering light and it shows completely"): while you're up in it or on
 * its stairs, a sheet of dark over the whole floor, the rafters' shadows across it, a small pool of light under each
 * bulb and round the candles (MUSEUM_ATTIC.lamps and .candles in common/src/definitions/buildings.ts); the bulbs
 * flicker, one of them badly, and go out now and then, their pictures dimming with them (1.04.2); the candles' flames
 * waver. Over the well above the security office there's no dark: the office below shows through it, lit, to whoever's
 * on the stairs or by the railing.
 *
 * The dark covers the boards and what lies or stands on them (the crates, the remains, the traps) and the spiders that
 * creep about on them (1.04.1; the owner, 25 October 2026: "spider looks too big, and doesnt fit with lightning really,
 * its like its over the lightning": seen where a light falls, as the rest); the loot, a grenade on the floor and the
 * players show over it (what has to be seen to be played), and the death markers (1.04.2). What's drawn over it that
 * gives no light (a smoke cloud, dust, splinters, the bits of a broken crate) is shaded as dark as the dark is where it
 * is (1.04.2's audit: they showed at full brightness in a black room); an explosion's flash shows over it, and lights it
 * round the blast for a moment. Round you there's a faint glow, the dark thinned a few steps about you (the eyes used to
 * it), so you see what's near as you creep about and the rest stays black: it follows you as you're drawn (1.04.2: it
 * followed the server's position, jumping ahead of you as you moved).
 *
 * The dark is drawn once (a canvas, 4 pixels a unit), as soon as the museum comes into sight (1.04.2: on the first climb,
 * a hitch on the stairs); each frame it's copied to a texture with the flicker's dark blob over each light's pool (its
 * alpha that light's dark), the glow round you and any explosion's flash erased from it. It's kept with the attic's
 * things, in the upper floor's container, and goes with it: stepping down off the stairs, that floor fades out over a
 * fifth of a second, and the dark stays over it till it's gone (1.04.2's audit: it went at once, and the attic showed
 * lit for that moment, every time anyone went down). Nothing here changes what the server sees: it's the page's look.
 *
 * At night (1.05: managers/nightLight.ts) the night's dark is cut away over the attic while its own shows, and every
 * flashlight up there lights it: its beam is erased out of the attic's dark too.
 */

interface Textures {
    readonly dark: Texture
    readonly blob: Texture
    readonly glow: Texture
    /** what's drawn each frame: the dark, the lights' flicker, the glow round you erased */
    readonly target: RenderTexture
    readonly scene: Container
    readonly dimmers: readonly Sprite[]
    readonly you: Sprite
}

/** an explosion's flash up there, lighting the dark round it while it lasts */
interface Flash {
    readonly x: number
    readonly y: number
    readonly radius: number
    readonly start: number
    readonly duration: number
    readonly sprite: Sprite
    strength: number
}

const MAX_FLASHES = 6;
/** just down from the attic or its stairs, this long, its floor's fading out still counts as being up there (ms) */
const FADING_MS = 1000;

const atticBuilding = (): Building | undefined => {
    for (const building of Game.objects.getCategory(ObjectCategory.Building)) {
        if (building.definition.idString === "dino_museum_attic" && !building.dead) return building;
    }
    return undefined;
};

/** when the page has a moment to spare (or soon, if it can't say) */
const whenIdle = (fn: () => void): void => {
    const idle = (globalThis as { requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number }).requestIdleCallback;
    if (idle) idle(fn, { timeout: 1500 });
    else setTimeout(fn, 0);
};

class AtticDarkClass {
    private _overlay?: Container;
    private _building?: Building;
    private _textures?: Textures;
    private _texturesAsked = false;
    /** whether the dark is shown this frame, and what's lit: each light's brightness, the glow round you, the flashes */
    private _shown = false;
    private _flicker: number[] = [];
    private _you?: { readonly x: number, readonly y: number, readonly glow: number };
    private readonly _flashes: Flash[] = [];
    /** the bulbs' pictures, by the light each hangs at (looked for again now and then: they come and go with the view) */
    private _bulbs = new Map<number, Obstacle>();
    private _bulbsAt = 0;
    /** the flashlights' beams up there at night, erased out of the dark (reused frame to frame) */
    private readonly _beams: Sprite[] = [];

    private _makeTextures(): Textures {
        const w = HALF_W * 2 * RES, h = HALF_H * 2 * RES;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        const px = (x: number): number => (x + HALF_W) * RES;
        const py = (y: number): number => (y + HALF_H) * RES;
        if (ctx) {
            ctx.fillStyle = `rgba(5, 4, 3, ${DARK})`;
            ctx.fillRect(0, 0, w, h);
            // the pools under the lights: cut out of the dark (to LIT at their middles), warmed a little
            for (const { x, y, pool, candle } of LIGHTS) {
                const g = ctx.createRadialGradient(px(x), py(y), 0, px(x), py(y), pool * RES);
                const cut = 1 - LIT / DARK;
                g.addColorStop(0, `rgba(0, 0, 0, ${cut})`);
                g.addColorStop(0.45, `rgba(0, 0, 0, ${cut * 0.6})`);
                g.addColorStop(1, "rgba(0, 0, 0, 0)");
                ctx.globalCompositeOperation = "destination-out";
                ctx.fillStyle = g;
                ctx.fillRect(px(x) - pool * RES, py(y) - pool * RES, 2 * pool * RES, 2 * pool * RES);
                const warm = ctx.createRadialGradient(px(x), py(y), 0, px(x), py(y), pool * RES * 0.8);
                warm.addColorStop(0, candle ? "rgba(255, 150, 60, 0.16)" : "rgba(255, 196, 110, 0.1)");
                warm.addColorStop(1, "rgba(255, 196, 110, 0)");
                ctx.globalCompositeOperation = "source-over";
                ctx.fillStyle = warm;
                ctx.fillRect(px(x) - pool * RES, py(y) - pool * RES, 2 * pool * RES, 2 * pool * RES);
            }
            // the rafters' shadows overhead: across the hall every 12 units, and the ridge beam along its middle
            ctx.globalCompositeOperation = "source-over";
            ctx.fillStyle = "rgba(0, 0, 0, 0.34)";
            for (let x = -HALF_W + 8; x < HALF_W; x += 12) ctx.fillRect(px(x), 0, 1.6 * RES, h);
            ctx.fillStyle = "rgba(0, 0, 0, 0.42)";
            ctx.fillRect(0, py(-1.4), w, 2.8 * RES);
            // the braces from the ridge, now and then
            ctx.strokeStyle = "rgba(0, 0, 0, 0.3)";
            ctx.lineWidth = 1.2 * RES;
            for (let x = -HALF_W + 14; x < HALF_W; x += 36) {
                ctx.beginPath();
                ctx.moveTo(px(x), py(0));
                ctx.lineTo(px(x + 12), py(-HALF_H));
                ctx.moveTo(px(x), py(0));
                ctx.lineTo(px(x + 12), py(HALF_H));
                ctx.stroke();
            }
            // the well over the security office: no dark at all (the office below, lit, through it)
            const [x0, y0, x1, y1] = MUSEUM_ATTIC.well;
            ctx.globalCompositeOperation = "destination-out";
            ctx.fillStyle = "#000";
            ctx.fillRect(px(x0), py(y0), (x1 - x0) * RES, (y1 - y0) * RES);
            ctx.globalCompositeOperation = "source-over";
        }
        const blobCanvas = document.createElement("canvas");
        const size = BULB_POOL * 2 * RES;
        blobCanvas.width = blobCanvas.height = size;
        const bctx = blobCanvas.getContext("2d");
        if (bctx) {
            const g = bctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
            g.addColorStop(0, "rgba(5, 4, 3, 1)");
            g.addColorStop(0.45, "rgba(5, 4, 3, 0.6)");
            g.addColorStop(1, "rgba(5, 4, 3, 0)");
            bctx.fillStyle = g;
            bctx.fillRect(0, 0, size, size);
        }
        const glowCanvas = document.createElement("canvas");
        const glowSize = GLOW_REACH * 2 * RES;
        glowCanvas.width = glowCanvas.height = glowSize;
        const gctx = glowCanvas.getContext("2d");
        if (gctx) {
            const g = gctx.createRadialGradient(glowSize / 2, glowSize / 2, 0, glowSize / 2, glowSize / 2, glowSize / 2);
            g.addColorStop(0, "rgba(255, 255, 255, 1)");
            g.addColorStop(0.35, "rgba(255, 255, 255, 0.75)");
            g.addColorStop(0.7, "rgba(255, 255, 255, 0.25)");
            g.addColorStop(1, "rgba(255, 255, 255, 0)");
            gctx.fillStyle = g;
            gctx.fillRect(0, 0, glowSize, glowSize);
        }
        const dark = Texture.from(canvas);
        const blob = Texture.from(blobCanvas);
        const glow = Texture.from(glowCanvas);
        // the frame's picture of it, in the canvas's own pixels
        const scene = new Container();
        scene.addChild(new Sprite(dark));
        const dimmers = LIGHTS.map(({ x, y, pool }) => {
            const sprite = new Sprite(blob);
            sprite.anchor.set(0.5);
            sprite.scale.set(pool / BULB_POOL);
            sprite.position.set(px(x), py(y));
            sprite.alpha = 0;
            scene.addChild(sprite);
            return sprite;
        });
        const you = new Sprite(glow);
        you.anchor.set(0.5);
        you.blendMode = "erase";
        scene.addChild(you);
        return { dark, blob, glow, target: RenderTexture.create({ width: w, height: h }), scene, dimmers, you };
    }

    private _build(): Container {
        this._textures ??= this._makeTextures();
        const overlay = new Container();
        overlay.zIndex = ATTIC_DARK_ZINDEX;
        const picture = new Sprite(this._textures.target);
        picture.anchor.set(0.5);
        picture.scale.set(PIXI_SCALE / RES);
        overlay.addChild(picture);
        return overlay;
    }

    /** a point in the attic's own units */
    private _local(building: Building, position: Vector): Vector {
        return Vec.rotate(Vec.sub(position, building.position), -building.rotation);
    }

    /**
     * where the active player is drawn in the attic's own units, if the dark shows: they're up in it or on its stairs, or
     * just off them, the attic's floor still fading out
     */
    private _where(building: Building): Vector | undefined {
        const player = Game.activePlayer;
        // (in a plane or under a chute nobody's in the attic: every roof shows from up there, the attic's floor with it,
        // and the night's dark was cut away over the whole museum as they flew over it: 1.06.1's audit)
        if (!player || player.air) return undefined;
        const now = performance.now();
        if (player.layer === Layer.Upstairs || player.layer === Layer.ToUpstairs) this._upAt = now;
        // (as they're drawn, smoothed between the server's updates)
        const drawn = Vec(player.container.position.x / PIXI_SCALE, player.container.position.y / PIXI_SCALE);
        const local = this._local(building, drawn);
        const floor = building.layerContainer ?? CameraManager.getContainer(Layer.Upstairs);
        // (the floor still fading out counts only just down from it: walking into the hall from outside, the attic's
        // floor fades too, and the night's dark went off the whole museum for a fifth of a second: 1.06.1's audit)
        const fading = now - this._upAt < FADING_MS ? floor : { visible: false, alpha: 0 };
        return darkShows(player.layer, inAtticArea(local.x, local.y, 1), fading) ? local : undefined;
    }

    /** when the active player was last up in the attic or on its stairs (performance.now()) */
    private _upAt = -Infinity;

    /**
     * where the attic's dark lies while it shows (the world's pixels: its middle, its turn and its size), for the night's
     * dark to be cut away there (managers/nightLight.ts)
     */
    area(): { readonly x: number, readonly y: number, readonly rotation: number, readonly width: number, readonly height: number } | undefined {
        const building = this._building;
        if (!this._shown || !building || !this._overlay?.visible) return undefined;
        const { x, y } = toPixiCoords(building.position);
        return { x, y, rotation: building.rotation, width: HALF_W * 2 * PIXI_SCALE, height: HALF_H * 2 * PIXI_SCALE };
    }

    /** whether something at `position` on `layer` is under the attic's dark now (it's shown, and that's up there) */
    covers(position: Vector, layer: Layer): boolean {
        const building = this._building;
        if (!this._shown || !building || layer !== Layer.Upstairs) return false;
        const local = this._local(building, position);
        return inAtticArea(local.x, local.y);
    }

    /**
     * whether the attic's floor hides what's at `position` under it, the attic shown (its boards everywhere but the
     * well over the security office): a light below, a flashlight's warm beam in the office, isn't seen up there
     * (1.06.1's audit: it was drawn over the boards)
     */
    floorHides(position: Vector): boolean {
        const building = this._building;
        if (!this._shown || !building) return false;
        const local = this._local(building, position);
        if (!inAtticArea(local.x, local.y)) return false;
        const [x0, y0, x1, y1] = MUSEUM_ATTIC.well;
        return !(local.x >= x0 && local.x <= x1 && local.y >= y0 && local.y <= y1);
    }

    /** how bright something drawn over the dark should be at `position` (1 if the dark isn't over it) */
    brightnessAt(position: Vector, layer: Layer): number {
        const building = this._building;
        if (!this._shown || !building || layer !== Layer.Upstairs) return 1;
        const local = this._local(building, position);
        if (!inAtticArea(local.x, local.y)) return 1;
        return 1 - darknessAt(local.x, local.y, this._flicker, this._you, this._flashes);
    }

    /** `tint` as dark as the attic's dark is at `position` (unchanged where it isn't) */
    shade(tint: number, position: Vector, layer: Layer): number {
        const brightness = this.brightnessAt(position, layer);
        return brightness >= 0.999 ? tint : shadeTint(tint, brightness);
    }

    /** an explosion: up there, its light shows through the dark round it for a moment (while the dark's shown) */
    flash(position: Vector, layer: Layer, radius: number, duration: number): void {
        const building = this._building;
        const textures = this._textures;
        if (!this._shown || !building || !textures || layer !== Layer.Upstairs) return;
        const local = this._local(building, position);
        if (!inAtticArea(local.x, local.y, radius)) return;
        while (this._flashes.length >= MAX_FLASHES) this._dropFlash(0);
        const sprite = new Sprite(textures.glow);
        sprite.anchor.set(0.5);
        sprite.blendMode = "erase";
        sprite.position.set((local.x + HALF_W) * RES, (local.y + HALF_H) * RES);
        sprite.scale.set(radius / GLOW_REACH);
        sprite.alpha = 0;
        textures.scene.addChild(sprite);
        this._flashes.push({ x: local.x, y: local.y, radius, start: Date.now(), duration, sprite, strength: 0 });
    }

    private _dropFlash(i: number): void {
        const [flash] = this._flashes.splice(i, 1);
        flash?.sprite.destroy();
    }

    private _hide(): void {
        this._shown = false;
        if (this._overlay) this._overlay.visible = false;
        while (this._flashes.length) this._dropFlash(0);
    }

    update(): void {
        const building = this._building !== undefined && !this._building.destroyed && Game.objects.has(this._building)
            ? this._building
            : (this._building = atticBuilding());
        if (!building) {
            this._hide();
            return;
        }
        // (its textures made as soon as the museum's in sight, at a quiet moment: made on the first climb, they were a
        // hitch on the stairs)
        if (!this._textures && !this._texturesAsked) {
            this._texturesAsked = true;
            whenIdle(() => { this._textures ??= this._makeTextures(); });
        }
        const you = this._where(building);
        if (!you) {
            this._hide();
            return;
        }
        if (this._overlay === undefined || this._overlay.destroyed) this._overlay = this._build();
        const textures = this._textures;
        if (!textures) return;
        // (with the attic's things, in the upper floor's container: an object up there is always in that one)
        const container = building.layerContainer ?? CameraManager.getContainer(Layer.Upstairs);
        if (this._overlay.parent !== container) container.addChild(this._overlay);
        // (where the attic is, every frame: an overlay kept from a match left is moved with the next one's attic)
        this._overlay.position.copyFrom(toPixiCoords(building.position));
        this._overlay.rotation = building.rotation;
        // (all of it on the stairs too: the attic shows from them as dark as it is, the office below through the well.
        // 1.03 drew half of it there, and the whole attic showed as you climbed)
        this._overlay.visible = true;
        this._shown = true;
        const t = Date.now();
        // (a light out: its pool covered back to the dark round it)
        this._flicker = flickerAt(t);
        textures.dimmers.forEach((blob, i) => {
            blob.alpha = (1 - this._flicker[i]) * LIGHT_OUT;
        });
        // the glow round you, wavering a little
        const glow = GLOW * (0.96 + 0.04 * Math.sin(t * 0.011));
        this._you = { x: you.x, y: you.y, glow };
        textures.you.position.set((you.x + HALF_W) * RES, (you.y + HALF_H) * RES);
        textures.you.alpha = glow;
        // the explosions' flashes: bright at once, fading out
        for (let i = this._flashes.length - 1; i >= 0; i--) {
            const flash = this._flashes[i];
            const k = (t - flash.start) / flash.duration;
            if (k >= 1) {
                this._dropFlash(i);
                continue;
            }
            flash.strength = (1 - k) * (1 - k);
            flash.sprite.alpha = flash.strength;
        }
        this._updateBeams(building, textures);
        // (on the Canvas 2D renderer the texture's canvas starts drawn normally: utils/canvasBlend.ts)
        renderInto(Game.pixi.renderer, textures.scene, textures.target);
        this._updateBulbs(building, t);
        this._updateMarkers(building);
    }

    /** at night, the flashlights up there or on the stairs: each beam erased out of the attic's dark too */
    private _updateBeams(building: Building, textures: Textures): void {
        let used = 0;
        for (const beam of NightLight.beams()) {
            if (beam.layer !== Layer.Upstairs && beam.layer !== Layer.ToUpstairs) continue;
            const local = this._local(building, Vec(beam.x / PIXI_SCALE, beam.y / PIXI_SCALE));
            if (!inAtticArea(local.x, local.y, Night.reach)) continue;
            let sprite = this._beams[used];
            if (!sprite) {
                sprite = new Sprite(nightTextures().beam);
                sprite.anchor.set(0, 0.5);
                sprite.blendMode = "erase";
                this._beams.push(sprite);
                textures.scene.addChild(sprite);
            }
            used++;
            sprite.visible = true;
            sprite.position.set((local.x + HALF_W) * RES, (local.y + HALF_H) * RES);
            sprite.rotation = beam.rotation - building.rotation;
            sprite.scale.set((Night.reach * RES) / BEAM_LENGTH);
            sprite.alpha = beam.alpha;
        }
        for (let i = used; i < this._beams.length; i++) this._beams[i].visible = false;
    }

    /** the bulbs' pictures, dimmed with their light (their glow is baked in: out, a bulb showed lit over a black pool) */
    private _updateBulbs(building: Building, t: number): void {
        let missing = false;
        for (const bulb of this._bulbs.values()) if (bulb.destroyed) missing = true;
        if (missing || this._bulbs.size < MUSEUM_ATTIC.lamps.length && t - this._bulbsAt > 500) {
            this._bulbsAt = t;
            this._bulbs = new Map();
            for (const obstacle of Game.objects.getCategory(ObjectCategory.Obstacle)) {
                if (obstacle.definition.idString !== "museum_attic_lamp" || obstacle.destroyed) continue;
                const local = this._local(building, obstacle.position);
                const i = MUSEUM_ATTIC.lamps.findIndex(([x, y]) => Math.hypot(x - local.x, y - local.y) < 1.5);
                if (i >= 0) this._bulbs.set(i, obstacle);
            }
        }
        for (const [i, bulb] of this._bulbs) {
            const level = Math.round(bulbShade(this._flicker[i] ?? 1) * 255);
            bulb.container.tint = (level << 16) | (level << 8) | level;
        }
    }

    /** a death marker up there shows over the dark (a skull and a name, to be seen) */
    private _updateMarkers(building: Building): void {
        for (const marker of Game.objects.getCategory(ObjectCategory.DeathMarker)) {
            if (marker.layer !== Layer.Upstairs) continue;
            const local = this._local(building, marker.position);
            if (inAtticArea(local.x, local.y)) marker.container.zIndex = ATTIC_LIGHT_ZINDEX;
        }
    }

    reset(): void {
        // (the textures and the frame's picture are kept for the next game: only the overlay showing them goes)
        this._overlay?.destroy({ children: true });
        this._overlay = undefined;
        this._building = undefined;
        this._shown = false;
        this._bulbs = new Map();
        this._upAt = -Infinity;
        while (this._flashes.length) this._dropFlash(0);
    }
}

export const AtticDark = new AtticDarkClass();
